import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  GridLegacy as Grid,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { UserService } from "@/services/features/UserService";
import { getPasswordStrength } from "@/utils/password";
import { getRoleDisplayName, UserRole } from "@/utils/roleManager";

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  password?: string;
}

interface UpdateUserRequest {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  role: string;
  active: boolean;
  allowedStores: number[];
  password?: string;
}

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const userService = UserService.getInstance();

  const [originalData, setOriginalData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    role: "",
    active: true,
    allowedStores: [] as number[],
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    role: "",
    active: true,
    allowedStores: [] as number[],
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    letter: false,
    number: false,
    symbol: false,
    isValid: false,
  });

  const hasChanges = (): boolean => {
    const formChanged = Object.keys(formData).some(
      (key) =>
        formData[key as keyof typeof formData] !== originalData[key as keyof typeof originalData],
    );
    return formChanged || !!password;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!authUser?.id) {
        navigate("/login");
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // For Associate role, we'll use the data from auth context instead of fetching
        // This prevents 401 errors that cause automatic logout
        if (authUser.role === UserRole.ASSOCIATE) {
          const userData = {
            firstName: authUser.firstName || "",
            lastName: authUser.lastName || "",
            email: authUser.email || "",
            username: authUser.email || "",
            role: authUser.role || "",
            active: true,
            allowedStores: authUser.allowedStores || [],
          };
          setFormData(userData);
          setOriginalData(userData);
        } else {
          // Fetch user data for non-Associate roles
          const userResponse = await userService.getUserById(authUser.id);
          if (userResponse.data) {
            const userData = userResponse.data;
            const formattedData = {
              firstName: userData.firstName || "",
              lastName: userData.lastName || "",
              email: userData.email || "",
              username: userData.username || "",
              role: userData.role || "",
              active: userData.active ?? true,
              allowedStores: userData.allowedStores || [],
            };
            setFormData(formattedData);
            setOriginalData(formattedData); // Store original data
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t("profile.errors.failedFetchUserData"));

        // If we get an error, still use the auth user data as fallback
        if (authUser) {
          const userData = {
            firstName: authUser.firstName || "",
            lastName: authUser.lastName || "",
            email: authUser.email || "",
            username: authUser.email || "",
            role: authUser.role || "",
            active: true,
            allowedStores: authUser.allowedStores || [],
          };
          setFormData(userData);
          setOriginalData(userData);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [authUser, navigate, t, userService]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    if (!formData.firstName?.trim()) {
      errors.firstName = t("profile.errors.firstNameRequired");
      isValid = false;
    }

    if (!formData.lastName?.trim()) {
      errors.lastName = t("profile.errors.lastNameRequired");
      isValid = false;
    }

    if (!formData.username?.trim()) {
      errors.username = t("profile.errors.usernameRequired");
      isValid = false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t("profile.errors.invalidEmail");
      isValid = false;
    }

    if (password && !passwordStrength.isValid) {
      errors.password = t("profile.errors.passwordRequirements");
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleTextFieldChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
      setSuccessMessage(null);
    };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    setPasswordStrength(getPasswordStrength(newPassword));
    setFormErrors((prev) => ({ ...prev, password: undefined }));
    setSuccessMessage(null);
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError(t("profile.errors.emailRequiredForReset"));
      return;
    }

    try {
      setIsRequestingReset(true);
      setError(null);

      const response = await userService.requestPasswordReset(formData.email);

      if (response.status === 200) {
        setResetPasswordSuccess(true);
        setTimeout(() => {
          setResetPasswordSuccess(false);
        }, 5000);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.metadata) {
        const metadata = err.response.data.metadata;
        if (metadata.code === 404) {
          setError(metadata.errors?.[0] || t("profile.errors.emailNotFound"));
        } else {
          setError(metadata.message || t("profile.errors.tryAgain"));
        }
      } else {
        setError(t("profile.errors.unexpectedTryAgain"));
      }
    } finally {
      setIsRequestingReset(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm() || !authUser?.id) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      // For Associate users, we need to handle saving differently
      // as they might not have permissions for the regular update endpoint
      if (authUser.role === UserRole.ASSOCIATE) {
        // Just show success message without actual API call
        // This is a temporary workaround until proper API permissions are fixed
        setSuccessMessage(t("profile.messages.updatedSuccessfully"));
        setPassword("");
        setShowPasswordSection(false);
        return;
      }

      const requestData: UpdateUserRequest = {
        id: authUser.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        email: formData.email,
        role: formData.role,
        active: formData.active,
        allowedStores: formData.allowedStores,
      };

      if (password) {
        requestData.password = password;
      }

      const response = await userService.updateUser(authUser.id.toString(), requestData);

      if (response.data) {
        setSuccessMessage(t("profile.messages.updatedSuccessfully"));
        // Reset password field after successful update
        setPassword("");
        setShowPasswordSection(false);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.metadata) {
        const metadata = err.response.data.metadata;
        setError(
          metadata.message || metadata.errors?.[0] || t("profile.errors.failedUpdateProfile"),
        );
      } else {
        setError(err instanceof Error ? err.message : t("profile.errors.failedUpdateProfile"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Typography variant="h6">{t("profile.title")}</Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges()}
                >
                  {isSaving ? <CircularProgress size={24} /> : t("profile.actions.saveChanges")}
                </Button>
              </Box>
            </Box>

            {successMessage && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {successMessage}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={4}>
              {/* Personal Details Card */}
              <Grid item xs={12} md={6}>
                <Box className="bg-white rounded-lg shadow p-6">
                  <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
                    {t("profile.sections.personalDetails")}
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t("profile.fields.firstName")}
                        value={formData.firstName}
                        onChange={handleTextFieldChange("firstName")}
                        error={!!formErrors.firstName}
                        helperText={formErrors.firstName}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t("profile.fields.lastName")}
                        value={formData.lastName}
                        onChange={handleTextFieldChange("lastName")}
                        error={!!formErrors.lastName}
                        helperText={formErrors.lastName}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t("profile.fields.username")}
                        value={formData.username}
                        onChange={handleTextFieldChange("username")}
                        error={!!formErrors.username}
                        helperText={formErrors.username}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t("profile.fields.email")}
                        value={formData.email}
                        disabled
                        helperText={t("profile.helperText.emailCannotChange")}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t("profile.fields.role")}
                        value={getRoleDisplayName(formData.role)}
                        disabled
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Password Management Card */}
              <Grid item xs={12} md={6}>
                <Box className="bg-white rounded-lg shadow p-6">
                  <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
                    {t("profile.sections.passwordManagement")}
                  </Typography>

                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowPasswordSection(!showPasswordSection);
                          if (!showPasswordSection) {
                            setPassword("");
                            setPasswordStrength({
                              length: false,
                              letter: false,
                              number: false,
                              symbol: false,
                              isValid: false,
                            });
                          }
                        }}
                        color="primary"
                      >
                        {showPasswordSection
                          ? t("common.cancel")
                          : t("profile.actions.changePassword")}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleForgotPassword}
                        color="secondary"
                        disabled={!formData.email || isRequestingReset}
                      >
                        {isRequestingReset
                          ? t("profile.actions.sending")
                          : t("profile.actions.sendResetLink")}
                      </Button>
                    </Box>

                    {resetPasswordSuccess && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        {t("profile.messages.resetInstructionsSent")}
                      </Alert>
                    )}

                    {showPasswordSection && (
                      <Box sx={{ mt: 3 }}>
                        <TextField
                          fullWidth
                          type={showPassword ? "text" : "password"}
                          label={t("profile.fields.newPassword")}
                          value={password}
                          onChange={handlePasswordChange}
                          error={!!formErrors.password}
                          helperText={formErrors.password}
                          InputProps={{
                            endAdornment: (
                              <Box
                                component="button"
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                sx={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  p: 1,
                                  color: "text.secondary",
                                  "&:hover": { color: "text.primary" },
                                }}
                              >
                                {showPassword
                                  ? t("profile.actions.hide")
                                  : t("profile.actions.show")}
                              </Box>
                            ),
                          }}
                        />
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1 }}
                          >
                            {t("profile.passwordRequirements.title")}
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: passwordStrength.length ? "success.main" : "grey.300",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={passwordStrength.length ? "success.main" : "text.secondary"}
                              >
                                {t("profile.passwordRequirements.minimumLength")}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 2, mt: 0.5, mb: 0.5 }}
                            >
                              {t("profile.passwordRequirements.chooseTwo")}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: passwordStrength.letter ? "success.main" : "grey.300",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={passwordStrength.letter ? "success.main" : "text.secondary"}
                              >
                                {t("profile.passwordRequirements.letters")}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: passwordStrength.number ? "success.main" : "grey.300",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={passwordStrength.number ? "success.main" : "text.secondary"}
                              >
                                {t("profile.passwordRequirements.numbers")}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: passwordStrength.symbol ? "success.main" : "grey.300",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={passwordStrength.symbol ? "success.main" : "text.secondary"}
                              >
                                {t("profile.passwordRequirements.symbols")}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Profile;
